/**
 * The player submersible (PRD §5/§6/§7).
 *
 *   input → thrust → acceleration → velocity → drag/buoyancy → position
 *
 * Deliberately arcade: no hydrodynamics, but enough momentum that the sub feels
 * heavy, and enough braking authority that it never feels sluggish.
 *
 * Everything that an upgrade can change lives in `stats`, which the world hands
 * in and replaces whenever a module is bought. The sub itself never reads a
 * capacity out of `config` directly.
 */

import { BREACH, CARGO, NITRO, POWER, SUB, WORLD, seaHeight } from './config';
import { GUN_LIMIT, PATHFINDER, type Chassis } from './chassis';
import { ModifierStack, resolveStats, statsFor, emptyLevels, type Stats } from './upgrades';
import { density, featuresFor, gradient } from './field';
import { toField } from './mesher';
import { angleDelta, clamp, damp, dampAngle, hyp, lerp, rotateOffset } from '../core/math';
import type { InputState } from '../core/input';

export interface Contact {
  x: number;
  y: number;
  nx: number;
  ny: number;
  speed: number;
}

const n = { x: 0, y: 0 };
/** Scratch for hull-local -> world hardpoint rotation. Never nested. */
const hpScratch = { x: 0, y: 0, z: 0 };

export class Player {
  /**
   * The boat this is. Everything about how it handles, where its hardpoints
   * are and what it is capable of stock comes from here — `SUB` is now only the
   * table the Pathfinder is built from, not a global truth about submarines.
   */
  chassis: Chassis;

  x = 0;
  y = 0;
  /**
   * Depth into the scene. Zero for the whole of the game's life until now, and
   * still zero unless the sub is travelling between parallax lanes.
   */
  z = 0;
  /** Lane the sub is inside, or -1 while crossing the water between lanes. */
  lane = 0;
  /** Scratch for `toField`, so collision allocates nothing per proxy. */
  private fieldPt = { x: 0, y: 0 };
  vx = 0;
  vy = 0;

  facing = 1;
  yaw = 0;
  pitch = 0;
  roll = 0;

  /**
   * Resolved stats: the chassis and its modules, with every active modifier
   * folded in. Replaced wholesale when a module is bought, a hull is swapped or
   * a modifier starts or ends.
   */
  stats: Stats = statsFor(emptyLevels());
  /** What the stats resolve FROM, before modifiers. */
  private baseStats: Stats = statsFor(emptyLevels());

  /**
   * Timed and run-scoped stat changes. One mechanism for the powerups that
   * already exist and for whatever Survival grants later — the alternative is
   * two systems that disagree about precedence the first time they overlap.
   */
  readonly mods = new ModifierStack();

  hull: number = SUB.maxHull;
  shield = 0;
  energy: number = SUB.maxEnergy;
  nitro: number = NITRO.max;
  torpedoes = 8;
  charges = 0;
  lives: number = SUB.startLives;
  score = 0;

  /**
   * Tonnes on the line right now. Set by the world before `update`, never by
   * the sub itself — the boat has no idea what a grapple is, it only knows it
   * has become heavy.
   */
  cargoMass = 0;

  /**
   * Seconds left on the two timed powerups. Derived from the modifier stack
   * rather than stored, so the dozen places that display a countdown ring or a
   * glow keep reading exactly what they always did while there is only one
   * clock underneath them.
   */
  get overdrive(): number { return this.mods.timeOf('overdrive'); }
  get aegis(): number { return this.mods.timeOf('aegis'); }

  propSpin = 0;
  thrustLevel = 0;
  lightOn = true;
  /** Aim of the searchlight, in radians, relative to +X. */
  lightAngle = 0;
  /**
   * How far the lamp housing is swivelled off the hull's own axis, in the
   * MESH's local pitch, not in world radians.
   *
   * `lightAngle` is a world heading and the hull's world heading jumps by π the
   * frame the boat turns around, so the difference between them jumps too —
   * which would snap the housing through a half turn while the hull was still
   * easing round. Kept in local space and damped, the swivel is continuous by
   * construction and the housing cannot leave the boat.
   */
  lampSwivel = 0;

  /**
   * WHERE THE GUN IS POINTING, as a world heading. The shot goes here.
   *
   * It used to not exist, and the cannon fired along `aimAngle()` — the
   * direction the HULL points. The boat has a turret on a swivel stand and it
   * was welded shut: to shoot at something above you, you had to fly at it.
   *
   * Held in world space and not as a local elevation, for the reason
   * `lampSwivel` gives right above and pays for again here: the local angle is
   * what the MESH wants, but the local angle is a difference between two
   * headings, and deriving the world heading back out of it means adding the
   * hull's — which jumps by π the frame the boat turns around. Keeping the
   * world angle and deriving the local one is the direction that survives a
   * turn, and it is the direction the lamp already goes.
   */
  gunAngle = 0;

  /**
   * The same aim as a HULL-LOCAL elevation: up positive, at both facings.
   *
   * This first copied `lampSwivel`'s convention, which carries a `* facing`,
   * on the grounds that the lamp housing had been swinging correctly for a
   * long time. It had not. `Lamp_Head` was being turned about the wrong axis
   * entirely — see `sub2PitchJoint` — and a searchlight housing is close
   * enough to a solid of revolution that yawing one instead of pitching it
   * looks like almost nothing. So the convention it had validated was
   * validated against nothing, and a gun barrel is not a solid of revolution.
   *
   * Hull-local and unflipped is the value BOTH consumers want: the mesh is
   * drawn through the hull's own half turn and the muzzle offset goes through
   * `rotateOffset`, which applies the same one. Two `* facing` factors deleted
   * rather than moved.
   *
   * It is also the CLAMP. The stand has a mechanical limit like any real
   * mount, and applying it here rather than to the demand means the limit
   * holds however the aim arrived — stick, pad, key, or the default
   * lamp-follow that has no idea a limit exists.
   */
  turretSwivel = 0;

  /**
   * The mount's stops, radians, up and down — read live off `GUN_LIMIT` so a
   * slider moves them without a reload.
   *
   * ASYMMETRIC, because the two ends fail differently: up runs the housing's
   * tail into the pedestal, down puts the shot through the bow. See
   * `GUN_LIMIT` in chassis.ts for the measurements both numbers come from.
   */
  static gunUp(): number { return (GUN_LIMIT.up * Math.PI) / 180; }
  static gunDown(): number { return (GUN_LIMIT.down * Math.PI) / 180; }

  /** True on the frames the nitro is actually burning. */
  nitroOn = false;
  /** 0..1, for the HUD and the camera. */
  nitroBlend = 0;

  /**
   * How much of the hull is in the water: 1 fully under, 0 fully clear.
   *
   * Read by the force integration below, and by the world for the wake, the
   * bubble trail and the engine note — all of which are about water, and none
   * of which should keep running when there is none.
   */
  submersion = 1;
  /** The local sea height this frame — the crest or trough the hull is on. */
  seaY: number = WORLD.surfaceY;
  /** Whether the hull's centre is under the waterline. Crossing it is an event. */
  private wet = true;
  /**
   * Signed crossing speed, m/s, on the ONE frame the hull crosses the surface:
   * positive leaving the water, negative re-entering, zero every other frame.
   * The world reads it to throw a splash, because nothing else in the frame
   * knows the boat just changed which fluid it is in.
   */
  breach = 0;

  /** Contacts generated this frame, for sparks/sediment (PRD §7). */
  contacts: Contact[] = [];
  lastImpact = 0;
  invuln = 0;
  dead = false;
  /** Set on the frame the shield collapses, so the world can flash it. */
  shieldBroke = false;
  /**
   * 0..1, decaying: the shield just took a hit.
   *
   * A shield that silently eats damage is a shield the player cannot see
   * working, and "why am I still alive" is the question the whole aegis visual
   * exists to answer. This drives a flare on the shell, and it is what makes a
   * membrane appear for a moment even when no aegis field is up.
   */
  shieldHit = 0;
  /** Seconds since the last damage; drives the shield recharge delay. */
  private quiet = 0;
  private rechargeDelay = 0;

  /** Collision proxy: spheres along the hull (PRD §7 — no pixel-perfect). */
  private proxies: readonly { o: number; r: number }[];

  constructor(chassis: Chassis = PATHFINDER) {
    this.chassis = chassis;
    this.proxies = chassis.proxies;
    this.hull = chassis.base.maxHull;
    this.energy = chassis.base.maxEnergy;
    this.nitro = chassis.base.nitroMax;
    this.applyStats(statsFor(emptyLevels(), chassis));
  }

  /**
   * Swap the hull. Modules and modifiers are the caller's problem: the fleet
   * decides what is fitted, and a run decides what is temporarily true.
   */
  setChassis(c: Chassis, stats: Stats) {
    this.chassis = c;
    this.proxies = c.proxies;
    this.applyStats(stats);
  }

  /** Full heal and refill — a fresh life, or a dock at a station. */
  spawn(x: number, y: number, invuln: number = SUB.respawnInvuln) {
    this.x = x;
    this.y = y;
    // Back to the near lane. Every spawn point — station, checkpoint, the start
    // of a dive — is a place in layer 0, so arriving there still holding the
    // depth of the lane you died in puts the sub inside a slab it is not in.
    this.z = 0;
    this.lane = 0;
    this.vx = 0;
    this.vy = 0;
    this.hull = this.stats.maxHull;
    this.shield = this.stats.maxShield;
    this.energy = this.stats.maxEnergy;
    this.nitro = this.stats.nitroMax;
    this.dead = false;
    this.invuln = invuln;
    this.mods.clear('timed');
    this.quiet = 99;
    this.rechargeDelay = 0;
  }

  /** Re-derive capacities after a purchase, keeping current fill fractions sane. */
  applyStats(s: Stats) {
    const hullFrac = this.hull / Math.max(1, this.stats.maxHull);
    this.baseStats = s;
    this.stats = resolveStats(s, this.mods.list);
    this.hull = Math.min(s.maxHull, Math.max(this.hull, hullFrac * s.maxHull));
    this.energy = Math.min(s.maxEnergy, this.energy);
    this.nitro = Math.min(s.nitroMax, this.nitro);
    this.torpedoes = Math.min(s.torpedoMax, this.torpedoes);
    if (!s.chargeUnlocked) this.charges = 0;
  }

  /**
   * Re-fold the modifiers over the unchanged base. Deliberately NOT
   * `applyStats`: that clamps hull, energy and nitro against the new maxima,
   * and `hull / maxHull * maxHull` is not exactly `hull` in floating point — so
   * running it every frame would drift the sub's health downward by rounding
   * and, worse, move `npm run trace`'s baseline. This only touches `stats`.
   */
  private refreshStats() {
    this.stats = resolveStats(this.baseStats, this.mods.list);
  }

  get speed() {
    return hyp(this.vx, this.vy);
  }

  get depth() {
    return Math.max(0, WORLD.surfaceY - this.y);
  }

  /** True while nothing can touch the sub. */
  get immune() {
    return this.invuln > 0 || this.stats.invulnerable;
  }

  /** World position of the forward weapon muzzle. */
  muzzle(out: { x: number; y: number }) {
    const H = this.chassis.hardpoints;
    // A turret sits off the aim axis, so its shot cannot be placed by a single
    // distance along that axis. Rotated the way `fairlead` rotates its own
    // offset — through the YAW-blended angle rather than `aimAngle`, because
    // `aimAngle` jumps by half a turn the instant `facing` flips while the
    // drawn hull takes ~0.15 s to swing round, and a muzzle that teleports
    // mid-turn puts shots outside the boat that fired them.
    if (H.muzzleUp !== undefined && H.muzzleFore !== undefined) {
      // THROUGH THE HULL'S OWN ROTATION, not a rotation in the XY plane.
      //
      // Turning the boat around is a YAW — a half turn about the VERTICAL axis
      // — so it maps local forward to world -X and leaves local UP alone.
      // Rotating the offset by an angle in the screen plane instead flips both,
      // which put the turret a metre BELOW the hull every time the boat faced
      // west: right nose-east, wrong nose-west, and no clue why from the code.
      //
      // `rotateOffset` is the same R = Rz(pitch)·Ry(yaw)·Rx(roll) that
      // `drawPlayer` uses to place every drawn hardpoint, so the shot now
      // leaves exactly where the emitter is drawn, at both facings, by
      // construction rather than by a second derivation that has to agree.
      // AND THE BARREL SWINGS, so the emitter is not where it was.
      //
      // The offset is small — the aperture sits about 0.58 m out from the
      // stand's pivot — but a gun at full elevation whose shot still leaves the
      // stowed barrel tip is half a metre wrong at the moment the player is
      // looking straight at it. Swinging the offset costs a sin and a cos.
      //
      // Straight off `turretSwivel`, with no sign to undo: it is hull-local and
      // up-positive, and `rotateOffset` below applies the hull's half turn to
      // the result — which is the same half turn the mesh is drawn through, so
      // the emitter and the drawn barrel move together by construction.
      let fore = H.muzzleFore, up = H.muzzleUp;
      if (H.turretFore !== undefined && H.turretUp !== undefined) {
        const e = this.turretSwivel;
        const c = Math.cos(e), sn = Math.sin(e);
        const bx = fore - H.turretFore, by = up - H.turretUp;
        fore = H.turretFore + bx * c - by * sn;
        up = H.turretUp + bx * sn + by * c;
      }
      rotateOffset(hpScratch, this.roll, this.yaw, this.pitch, fore, up, 0);
      out.x = this.x + hpScratch.x;
      out.y = this.y + hpScratch.y;
      return out;
    }
    const c = Math.cos(this.aimAngle()), s = Math.sin(this.aimAngle());
    out.x = this.x + c * H.muzzle;
    out.y = this.y + s * H.muzzle;
    return out;
  }

  /**
   * Where the tow line leaves the boat — aft, so a load trails behind rather
   * than being dragged through the hull.
   *
   * Deliberately NOT built on `aimAngle()`, unlike `muzzle()`. `aimAngle` flips
   * from `pitch` to `PI - pitch` the instant `facing` changes, which moves a
   * point 1.86 m aft of the hull by 3.7 m in a single frame — more than
   * `GRAPPLE.snap`. The line would part every time the player turned around,
   * which is the one manoeuvre every haul requires: everything heavy has to
   * come back up the shaft it was found in.
   *
   * `yaw` is damped toward the same two targets over ~0.15 s, so deriving the
   * angle from it swings the tail round the way the *rendered* hull does. `t`
   * runs 0 → 1 as the boat turns, and the blend equals `aimAngle()` exactly at
   * both ends.
   */
  fairlead(out: { x: number; y: number }) {
    // Slightly aft of centre and below the keel: a winch port on the belly,
    // where a salvage boat would actually carry one. The line therefore leaves
    // the hull downwards and behind, instead of out of the nose and back
    // through the whole boat, which is what firing from the muzzle looked like.
    //
    // THROUGH THE HULL'S ROTATION, for the reason `muzzle` above spells out.
    // Rotating the offset by a single angle in the screen plane got the AFT
    // component right at both facings and inverted the DROP: nose-west the
    // winch port sat 1.58 m above the deck, and the line paid out over the
    // conning tower. Turning the boat around is a yaw about the vertical, so
    // below-the-keel stays below the keel.
    //
    // The yaw-blended reasoning below is preserved, and it still matters: this
    // deliberately does NOT use `aimAngle()`, which flips by half a turn the
    // instant `facing` changes and would move this point 3.7 m in one frame —
    // more than `GRAPPLE.snap`, parting the line on every turn. `yaw` is damped
    // over ~0.15 s, so feeding it to the same rotation the hull is DRAWN with
    // swings the port the way the drawn hull swings.
    const H = this.chassis.hardpoints;
    rotateOffset(hpScratch, this.roll, this.yaw, this.pitch, -H.fairleadAft, -H.fairleadDrop, 0);
    out.x = this.x + hpScratch.x;
    out.y = this.y + hpScratch.y;
    return out;
  }

  /**
   * Direction the sub is pointing, in radians.
   *
   * `pitch` is stored with the facing flip already baked in, because the mesh
   * is yawed 180 degrees when the boat turns and its local pitch axis inverts
   * with it — `drawPlayer` multiplies by `facing` a second time to undo that.
   * This used to flip a *third* time via `PI - pitch`, so the aim ended up
   * mirrored vertically whenever the boat was facing left: diving west, the gun
   * pointed up. Diving east it was correct, which is exactly the inconsistency
   * that gets reported.
   *
   * `pitch * facing` is the world-space pitch — the same value the mesh is
   * actually drawn with — and the aim is that, mirrored in x only.
   *
   * This is CORRECT and `scripts/trace.mjs` asserts it: the vertical component
   * of the aim agrees in sign with the vertical velocity whichever way the hull
   * faces. What was wrong for a long time is the MESH, which was composed with
   * `pitch * facing` and so pitched the opposite way whenever the boat faced
   * west — `compose` builds R = Rz·Ry·Rx, so the half turn that reverses the
   * boat is applied BEFORE the pitch and lands the forward vector at `rz + π`
   * rather than `π − rz`. See `drawPlayer`.
   */
  aimAngle(): number {
    const p = this.pitch * this.facing;
    return this.facing > 0 ? p : Math.PI - p;
  }

  update(input: InputState, dt: number, clock = 0) {
    this.contacts.length = 0;
    this.lastImpact = 0;
    this.shieldBroke = false;
    if (this.shieldHit > 0) this.shieldHit = Math.max(0, this.shieldHit - dt * 1.7);
    if (this.invuln > 0) this.invuln -= dt;
    // One clock for every timed effect. `update` only reports true when a
    // modifier actually started or ended, so an empty stack never re-resolves
    // and never touches a single float — which is what keeps the trace golden.
    if (this.mods.update(dt)) this.refreshStats();
    this.quiet += dt;

    const S = this.stats;
    const ix = input.moveX;
    const iy = input.moveY;

    // --- nitro -------------------------------------------------------------
    // Holding the key burns while there is fuel; it cannot be re-lit off an
    // almost-empty tank, which stops a tapped key returning free speed.
    const wants = input.boost && !this.dead;
    if (wants && this.nitro > (this.nitroOn ? 0.5 : NITRO.minToStart)) {
      this.nitroOn = true;
      this.nitro = Math.max(0, this.nitro - S.nitroDrain * dt);
    } else {
      this.nitroOn = false;
      this.nitro = Math.min(S.nitroMax, this.nitro + S.nitroRegen * dt);
    }
    this.nitroBlend = damp(this.nitroBlend, this.nitroOn ? 1 : 0, 7, dt);

    const boost = this.nitroOn ? NITRO.thrust * S.nitroPush : 1;
    const dragMul = this.nitroOn ? NITRO.drag : 1;

    // --- forces -----------------------------------------------------------
    // Load factor. Every term here is already an acceleration, so weight is
    // expressed by dividing them rather than by re-authoring SUB as forces —
    // which keeps terminal speed (thrust/drag) invariant, and therefore keeps
    // `camera.ts`'s maxSpeed, the attitude normalisers below and the whole
    // balance table correct without re-tuning any of them.
    //
    // At cargoMass 0 every line below reduces *exactly* to what it replaced —
    // `load` is 1.0 and `m` is 1.0, and multiplying or dividing by 1.0 is
    // lossless. The operand order is preserved for the same reason: floating
    // point multiplication is not associative, and `npm run trace` compares
    // against a baseline taken before any of this existed.
    const H = this.chassis.handling;
    const dry = this.chassis.dryMass;
    const load = dry + this.cargoMass;
    const m = load / dry;

    // --- how much boat is in the water ------------------------------------
    //
    // Measured over the hull's own diameter, so a boat sitting exactly on the
    // waterline is half in: it goes to 0 a radius clear and to 1 a radius
    // under, and the handover is therefore continuous rather than a switch you
    // can feel. Everything downstream scales by this, which is why there is no
    // frame where the boat is briefly neither swimming nor falling.
    // The waterline is where the SWELL is, not where the still level is. With
    // the sea running two metres peak to trough, a hull trimmed to a flat
    // `surfaceY` sits rigid while the wave washes over it — drawn water and
    // simulated water in the same frame, disagreeing, which is worse than
    // having no waves at all. Reading the same function the composite draws
    // means the boat rides the swell for free: it lifts on a crest, settles in
    // a trough, and its buoyancy follows because buoyancy is computed from this.
    this.seaY = WORLD.surfaceY + seaHeight(this.x, this.z, clock);
    const sub = clamp(
      (this.seaY - this.y) / (this.chassis.radius * 2) + 0.5, 0, 1,
    );
    this.submersion = sub;
    const air = 1 - sub;
    // Written as conditionals rather than as `1 - air * k` so that the fully
    // submerged case multiplies by a LITERAL 1.0, which IEEE guarantees is
    // lossless. `mix(k, 1, sub)` at sub = 1 is `k + (1 - k)`, which is not
    // exactly 1 for most k — and the trace baseline compares bit patterns.
    const bite = air > 0 ? 1 - air * BREACH.thrustLoss : 1;
    const fluid = air > 0 ? 1 - air * BREACH.dragLoss : 1;

    let ax = ix * H.thrustX * S.thrustMul * boost * bite / m;
    let ay = iy * H.thrustY * S.thrustMul * boost * bite / m;
    // Buoyancy fights the load's weight head on. Neutral trim lands near 0.6 t;
    // past that the boat sinks whenever you stop pushing, which is how you feel
    // what you are carrying without looking at a gauge.
    ay += (H.buoyancy * dry - CARGO.sinkAccel * this.cargoMass) * sub / load;
    // And weight, which the submerged model never needed a term for: `buoyancy`
    // is already the NET of displacement against gravity, so underwater this
    // subtracts a literal zero. Out of the water there is nothing left to do
    // the displacing, and what is left is the arc.
    ay -= BREACH.gravity * air;

    // Drag, with extra braking authority when the stick is released or reversed.
    const brakeX = ix === 0 ? H.brakeBoost : Math.sign(ix) !== Math.sign(this.vx) ? 1.5 : 1;
    const brakeY = iy === 0 ? H.brakeBoost * 0.8 : Math.sign(iy) !== Math.sign(this.vy) ? 1.4 : 1;
    const slip = 1 / S.topSpeedMul;
    ax -= this.vx * H.dragX * brakeX * dragMul * slip * fluid / m;
    ay -= this.vy * H.dragY * brakeY * dragMul * slip * fluid / m;

    // The load's own drag. Unlike everything above, this one is NOT cancelled
    // by the extra mass — drag depends on how much water a thing has to push
    // out of the way, not on how heavy it is. This is the term that actually
    // costs top speed, rather than only costing acceleration.
    if (this.cargoMass > 0) {
      const towDrag = this.cargoMass * CARGO.towDrag * S.haulMul;
      ax -= this.vx * towDrag / m;
      ay -= this.vy * towDrag / m;
    }

    this.vx += ax * dt;
    this.vy += ay * dt;

    // --- integrate with substeps so we never tunnel through thin rock -----
    const step = Math.max(0.35, this.chassis.radius * 0.55);
    const dist = hyp(this.vx, this.vy) * dt;
    const steps = Math.min(10, Math.max(1, Math.ceil(dist / step)));
    const sdt = dt / steps;
    for (let s = 0; s < steps; s++) {
      this.x += this.vx * sdt;
      this.y += this.vy * sdt;
      this.resolve();
    }

    // --- the waterline ----------------------------------------------------
    //
    // This was `y = surfaceY - 2.2` with a bounce: a hard lid two metres under,
    // there since the beginning and never questioned, which is why the sky the
    // renderer had just learned to draw could only ever be squinted at from
    // below. There is no lid now. The forces above already know which fluid the
    // hull is in, so crossing is something the integration does on its own and
    // all that is left here is to NOTICE it — a hull entering water at eight
    // metres a second has to make a splash, and nothing else in the frame has
    // any way to know it happened.
    const wasWet = this.wet;
    this.wet = this.y < this.seaY;
    this.breach = this.wet === wasWet
      ? 0
      : (wasWet ? Math.abs(this.vy) : -Math.abs(this.vy));

    // A ceiling nothing in normal play reaches — see `BREACH.ceiling`. Gravity
    // has already settled every ordinary arc; this is only here so a mine under
    // the keel cannot throw the boat out of the world.
    const roof = WORLD.surfaceY + BREACH.ceiling;
    if (this.y > roof) {
      this.y = roof;
      if (this.vy > 0) this.vy = 0;
    }

    // --- attitude ---------------------------------------------------------
    if (ix > 0.15) this.facing = 1;
    else if (ix < -0.15) this.facing = -1;

    const targetYaw = this.facing > 0 ? 0 : Math.PI;
    this.yaw = dampAngle(this.yaw, targetYaw, H.yawRate, dt);

    // Normalised against this hull's own terminal speed, not the Pathfinder's.
    // A Dray at full tilt should look like it is at full tilt.
    const vyN = clamp(this.vy / this.chassis.terminalY, -1, 1);
    const targetPitch = vyN * H.pitchMax * this.facing;
    this.pitch = damp(this.pitch, targetPitch, H.attitudeRate, dt);

    const vxN = clamp(this.vx / this.chassis.terminalX, -1, 1);
    const turn = clamp(ix - vxN, -1, 1);
    this.roll = damp(this.roll, -turn * H.bankMax * this.facing, H.attitudeRate * 0.8, dt);

    // --- systems ----------------------------------------------------------
    this.thrustLevel = damp(this.thrustLevel, Math.min(1, hyp(ix, iy)), 7, dt);
    this.propSpin += (2 + this.thrustLevel * 26 + this.speed * 0.5) * dt;
    this.energy = Math.min(S.maxEnergy, this.energy + S.energyRegen * dt);

    // Shields come back on their own once the shooting stops.
    if (this.rechargeDelay > 0) this.rechargeDelay -= dt;
    if (this.rechargeDelay <= 0 && this.quiet >= S.shieldDelay && this.shield < S.maxShield) {
      this.shield = Math.min(S.maxShield, this.shield + S.shieldRegen * dt);
    }

    // Searchlight leads the sub, biased by the stick so the player can aim it.
    const want = Math.atan2(clamp(iy * 0.7 + this.vy * 0.03, -1, 1), this.facing > 0 ? 1 : -1);
    this.lightAngle = dampAngle(this.lightAngle, want, 5.5, dt);
    // The housing's own swivel, in the mesh's pitch axis. Clamped so the lamp
    // can look where the beam goes without ever folding back into the bow.
    //
    // No `* facing` here: the yaw contributes a constant half turn to the
    // forward vector rather than a mirror, so a degree of local pitch is a
    // degree of world heading whichever way the boat faces. Multiplying by
    // facing — which is what mirroring would need — swung the housing the wrong
    // way every time the boat turned around, which is the visible half of the
    // same mistake `aimAngle` was making.
    const swivel = clamp(angleDelta(this.aimAngle(), this.lightAngle), -1.0, 1.0);
    this.lampSwivel = damp(this.lampSwivel, swivel, 9, dt);

    // --- the gun, which is no longer the hull ------------------------------
    //
    // Two ways in, and they are not the same kind of thing.
    //
    // With a real demand — the phone's fire stick, or a pad's right stick —
    // the elevation is commanded outright, off the hull's own heading. `*
    // facing` because `aimAngle` is `π - pitch` when the boat faces west, so
    // the angle runs backwards there and a raw addition would aim DOWN when
    // the player pushed up. Same trap `aimAngle` itself fell into once.
    //
    // With no demand it follows `lightAngle`, which is where the searchlight
    // and the turret head were ALREADY being drawn pointing. That is the older
    // half of this bug: the model has aimed the gun off-axis for as long as it
    // has had a turret, and the shot has been ignoring it and leaving along
    // the keel. A keyboard therefore gains a swivel without gaining a keybind
    // — push the stick up and the gun goes where the lamp goes, because the
    // lamp was always going somewhere the gun should have followed.
    const aim = this.aimAngle();
    const gunUp = Player.gunUp(), gunDown = Player.gunDown();
    // The demand is scaled by the stop it is heading FOR, not by one of them,
    // so full stick reaches the limit in both directions rather than reaching
    // the up limit and hitting the down one two thirds of the way along.
    const gy = clamp(input.gunY, -1, 1);
    const gunWant = input.gunAim
      ? aim + gy * (gy >= 0 ? gunUp : gunDown) * this.facing
      : this.lightAngle;
    // Faster than the lamp's 9: a light that lags reads as a heavy housing,
    // a gun that lags reads as unresponsive.
    this.gunAngle = dampAngle(this.gunAngle, gunWant, 12, dt);
    // Clamp in LOCAL space and rebuild the world angle from it, so the stop is
    // a property of the mount rather than of whichever input reached it. The
    // fallback path needs this as much as the stick does: `lightAngle` knows
    // nothing about a turret and a steep dive can carry it past the stop.
    this.turretSwivel = clamp(
      angleDelta(aim, this.gunAngle) * this.facing, -gunDown, gunUp,
    );
    this.gunAngle = aim + this.turretSwivel * this.facing;

    if (this.hull <= 0 && !this.dead) this.dead = true;
  }

  /**
   * Push the hull back out of any rock it is inside, and bleed off the speed it
   * arrived with. Public because the tow constraint moves the sub *after*
   * `update` has already integrated and resolved — without a second pass, a
   * load hanging below the boat could drag it a metre into the seabed and leave
   * it there until the next frame, which reads as the hull chattering against
   * the ground whenever you tow something heavy uphill.
   */
  resolveTerrain() {
    this.resolve();
  }

  private resolve() {
    const H = this.chassis.handling;
    const R = this.chassis.radius;
    const a = this.aimAngle();
    const dirX = Math.cos(a), dirY = Math.sin(a);
    let cx = 0, cy = 0, count = 0;

    // Between lanes there is nothing to hit: the slabs are 36 and 42 m apart
    // and that gap is water, so crossing it is meant to be unobstructed.
    if (this.lane < 0) return;

    for (const p of this.proxies) {
      const px = this.x + dirX * p.o;
      const py = this.y + dirY * p.o;
      // Sample the lane the sub is actually in. On layer 0 this is the identity
      // — warp 0, lift 0 — so the play lane behaves exactly as it always has.
      const f = toField(this.lane, px, py, this.fieldPt);
      const feats = featuresFor(f.x - p.r - 1, f.x + p.r + 1);
      const d = density(f.x, f.y, feats);
      if (d <= -p.r) continue;

      const gl = gradient(f.x, f.y, n);
      const pen = (d + p.r) / Math.max(gl, 0.35);
      if (pen <= 0) continue;

      cx += n.x * pen;
      cy += n.y * pen;
      count++;
    }

    if (!count) return;
    cx /= count;
    cy /= count;
    const cl = hyp(cx, cy) || 1;
    const nx = cx / cl, ny = cy / cl;

    this.x += cx * 0.92;
    this.y += cy * 0.92;

    const vn = this.vx * nx + this.vy * ny;
    if (vn < 0) {
      const impact = -vn;
      this.lastImpact = Math.max(this.lastImpact, impact);
      // Reflect the normal component, keep most of the tangential slide.
      const vnx = nx * vn, vny = ny * vn;
      const tx = this.vx - vnx, ty = this.vy - vny;
      const keep = lerp(1, H.scrapeFriction, clamp(impact / 20, 0, 1));
      this.vx = -vnx * H.bounce + tx * keep;
      this.vy = -vny * H.bounce + ty * keep;

      this.contacts.push({ x: this.x - nx * R, y: this.y - ny * R, nx, ny, speed: impact });

      if (impact > H.safeImpactSpeed) {
        this.hit((impact - H.safeImpactSpeed) * H.impactDamage * this.stats.impactMul, 0.35);
      }
    } else if (hyp(this.vx, this.vy) > 3) {
      // Scraping along a wall: sediment, no damage.
      this.contacts.push({
        x: this.x - nx * R, y: this.y - ny * R,
        nx, ny, speed: hyp(this.vx, this.vy) * 0.25,
      });
    }
  }

  // --- damage ---------------------------------------------------------------

  /**
   * A discrete hit: a bullet, a blast, a bite, a wall at speed. Buys a slice of
   * invulnerability so two threats landing on the same frame cannot delete the
   * sub, and always spends the shield before the hull.
   */
  hit(amount: number, iframes: number = SUB.hitInvuln): number {
    if (this.immune || this.dead || amount <= 0) return 0;
    this.invuln = Math.max(this.invuln, iframes);
    return this.applyDamage(amount);
  }

  /**
   * Continuous damage — stings, scrapes, standing in a vent plume. Already
   * scaled by dt by the caller, and deliberately outside the i-frame system so
   * that sitting inside a hazard is never free.
   */
  graze(amount: number): number {
    if (this.aegis > 0 || this.dead || amount <= 0) return 0;
    return this.applyDamage(amount);
  }

  private applyDamage(amount: number): number {
    this.quiet = 0;
    let left = amount;
    if (this.shield > 0) {
      const soaked = Math.min(this.shield, left);
      this.shield -= soaked;
      left -= soaked;
      // Scaled by the size of the hit, so a swarmer's nibble is a ripple and a
      // torpedo is a flash.
      // Was 0.35, which decayed to nothing in about an eighth of a second —
      // eight frames of a faint shell on a boat that is being shot at. The one
      // thing this visual exists to answer is "why am I still alive", and an
      // answer nobody sees is not an answer.
      this.shieldHit = Math.min(1, this.shieldHit + 0.6 + soaked * 0.03);
      if (this.shield <= 0.001) {
        this.shield = 0;
        this.shieldBroke = true;
        this.rechargeDelay = 1.8;
      }
    }
    if (left > 0) this.hull -= left;
    if (this.hull <= 0) {
      this.hull = 0;
      this.dead = true;
    }
    return amount;
  }

  /** Kept for callers that do not care about the discrete/continuous split. */
  damage(amount: number) {
    this.hit(amount);
  }

  // --- pickups --------------------------------------------------------------

  repair(n: number) {
    this.hull = Math.min(this.stats.maxHull, this.hull + n);
  }

  rechargeShield() {
    this.shield = this.stats.maxShield;
    this.rechargeDelay = 0;
  }

  addNitro(n: number) {
    this.nitro = Math.min(this.stats.nitroMax, this.nitro + n);
  }

  addEnergy(n: number) {
    this.energy = Math.min(this.stats.maxEnergy, this.energy + n);
  }

  addTorpedoes(n: number) {
    this.torpedoes = Math.min(this.stats.torpedoMax, this.torpedoes + n);
  }

  addCharges(n: number) {
    if (!this.stats.chargeUnlocked) return;
    this.charges = Math.min(this.stats.chargeMax, this.charges + n);
  }

  grantOverdrive(t: number = POWER.overdrive.time) {
    // What used to be three ternaries scattered through the firing code is one
    // declaration of what an overdrive actually does.
    this.mods.grant({
      id: 'overdrive', source: 'timed', time: t,
      mul: { cannonCooldown: POWER.overdrive.rate, cannonDamage: POWER.overdrive.damage },
      set: { cannonEnergy: POWER.overdrive.energy },
    });
    this.refreshStats();
  }

  grantAegis(t: number = POWER.aegis.time) {
    this.mods.grant({ id: 'aegis', source: 'timed', time: t, flag: { invulnerable: true } });
    this.refreshStats();
  }
}
